// src/components/FlipbookPreview.jsx
// UPDATED: Client-side PDF generation with watermark support
// Maintains privacy - no cloud upload required!
// --- ADDED: Elegant "Polished Progress" overlay for PDF generation ---
// --- ADDED: Native Android/iOS PDF saving via Capacitor Filesystem ---
// --- FIXED: Overlay moved outside containerRef to prevent layout interference during PDF capture ---
// --- FIXED: Share now properly attaches PDF file on Android/iOS ---
// --- ADDED: Admin print-quality PDF generation (long-press on Order Printed Book) ---

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { templateFor } from '../utils/pastTripsAlbumTemplate';
import { generatePDFClientSide, downloadPDF } from '../utils/clientSidePDF';
import html2canvas from 'html2canvas';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
// Firebase imports for admin print PDF upload
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getApp } from 'firebase/app';

// ✅ NEW: Subscription hook
import { useSubscription } from '../hooks/useSubscription';

// Add this helper function at the top of the file:
function waitForAllCanvasesToLoad(container) {
  const canvases = container.querySelectorAll('canvas');
  const promises = Array.from(canvases).map(canvas => {
    return new Promise((resolve) => {
      // Check if canvas is already drawn (width > 0 means it has content)
      if (canvas.width > 0 && canvas.height > 0) {
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, 1, 1);
        // If any pixel has data, canvas is ready
        if (imageData.data.some(val => val > 0)) {
          resolve();
          return;
        }
      }
      
      // Otherwise poll until ready (max 5 seconds)
      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (canvas.width > 0 && canvas.height > 0) {
          const ctx = canvas.getContext('2d');
          const imageData = ctx.getImageData(0, 0, 1, 1);
          if (imageData.data.some(val => val > 0)) {
            clearInterval(checkInterval);
            resolve();
          }
        }
        
        // Timeout after 5 seconds
        if (Date.now() - startTime > 5000) {
          clearInterval(checkInterval);
          console.warn('Canvas load timeout for:', canvas);
          resolve(); // Resolve anyway to not block
        }
      }, 100);
    });
  });
  
  return Promise.all(promises);
}

// Helper: Convert Blob to base64 string (for Capacitor Filesystem)
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // Result is "data:application/pdf;base64,XXXX" - we need just the base64 part
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Helper: Save PDF on native platforms (Android/iOS)
// FIXED: Use Cache directory for sharing (more permissive) + Documents for permanent storage
async function saveAndSharePDFNative(pdfBlob, fileName) {
  try {
    const base64Data = await blobToBase64(pdfBlob);
    const fileSizeMB = (pdfBlob.size / 1024 / 1024).toFixed(1);
    
    // Save to BOTH locations:
    // 1. Cache (for sharing - Android requires this for share intents)
    // 2. Documents (for permanent user access)
    
    // First, save to Cache for sharing
    const cacheResult = await Filesystem.writeFile({
      path: fileName,
      data: base64Data,
      directory: Directory.Cache,
      recursive: true
    });
    
    // Also save to Documents for permanent access
    let documentsUri = null;
    try {
      const docsResult = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Documents,
        recursive: true
      });
      documentsUri = docsResult.uri;
      console.log('✅ PDF saved to Documents:', documentsUri);
    } catch (docError) {
      console.warn('Could not save to Documents (will use Cache only):', docError);
    }
    
    console.log('✅ PDF saved to Cache for sharing:', cacheResult.uri);
    
    // Ask user if they want to share
    const wantsToShare = window.confirm(
      `✅ PDF Saved Successfully!\n\n` +
      `📁 Location: Documents/${fileName}\n` +
      `📊 Size: ${fileSizeMB} MB\n\n` +
      `Would you like to share this PDF now?`
    );
    
    if (wantsToShare) {
      try {
        // Use the Cache URI for sharing - this is more reliably shareable on Android
        // The 'files' array with Cache directory works with Android's FileProvider
        await Share.share({
          title: 'My VivaJour Album',
          text: 'Check out my travel album that I created with VivaJour! 📸✨',
          files: [cacheResult.uri],  // Use cache URI - more shareable
          dialogTitle: 'Share your album PDF'
        });
        console.log('✅ Share dialog opened with file');
      } catch (shareError) {
        // User cancelled share or share failed
        if (!shareError?.message?.toLowerCase().includes('cancel')) {
          console.warn('Share with file failed:', shareError);
          
          // Fallback: Alert user where to find the file
          alert(
            `📁 Your PDF is saved!\n\n` +
            `To share manually:\n` +
            `1. Open "Files" or "My Files" app\n` +
            `2. Go to Documents folder\n` +
            `3. Find "${fileName}"\n` +
            `4. Tap and choose Share`
          );
        }
      }
    } else {
      // User chose not to share - just confirm where to find it
      alert(
        `📁 PDF saved to Documents!\n\n` +
        `Find it in your Files app under:\n` +
        `Documents/${fileName}`
      );
    }
    
    return { success: true, uri: documentsUri || cacheResult.uri };
  } catch (error) {
    console.error('❌ Native PDF save failed:', error);
    throw error;
  }
}

// Print-quality settings for admin PDF generation
const PRINT_SETTINGS = {
  scale: 4,           // 4x scale for ~300 DPI effective
  quality: 0.95,      // Near-lossless JPEG
  addWatermark: false,
  label: 'Print Quality (300 DPI)'
};

// Admin password for print-quality PDF access
const ADMIN_PRINT_PASSWORD = 'Vivida2025';

// Admin mode check with password
const checkAdminPassword = () => {
  const password = window.prompt(
    '🔐 Admin Access Required\n\nEnter password for print-quality PDF:'
  );
  return password === ADMIN_PRINT_PASSWORD;
};

export default function FlipbookPreview({
  pages = [],
  current = 0,
  onChange,
  onShare,            // ← NEW
  onPreview,          // ← NEW: video-story preview
  width = 1024,
  height = 680,
  photos = [],
  pageWidth = 1024,
  pageHeight = 680,
  debug = false,
  showButtons = true,  // ← ADD THIS LINE
  showNav = true, // <-- ADD THIS LINE
  fillContainer = false,  // ← ADD THIS
  offsetX = 0,  // ← ADD THIS: horizontal offset in pixels
  offsetY = 0,  // ← ADD THIS: vertical offset in pixels
  shareBusy = false,
  previewBusy = false,
}) {
  const [index, setIndex] = useState(safeIndex(current, pages.length));
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  // ===== NEW STATE =====
  // Tracks progress for the new overlay
  const [pdfProgress, setPdfProgress] = useState({ current: 0, total: 0 });
  // =====================
  
  // Admin print PDF state
  const [isUploadingPrint, setIsUploadingPrint] = useState(false);
  const longPressTimerRef = useRef(null);
  const isLongPressRef = useRef(false);
  
  // ✅ NEW: Subscription hook
  const { isPro, openUpgradeModal } = useSubscription();
  
  // Detect Android (WebView or Chrome on Android)
  const isAndroid =
    typeof navigator !== 'undefined' &&
    /Android/i.test(navigator.userAgent);
  
  useEffect(() => setIndex(safeIndex(current, pages.length)), [current, pages.length]);


  useEffect(() => setIndex(safeIndex(current, pages.length)), [current, pages.length]);

  const total = pages.length;
  const page = pages[index] || null;

  const photoIndex = useMemo(() => {
    if (Array.isArray(photos)) {
      return Object.fromEntries(photos.map((p) => [p.id, p]));
    }
    return photos || {};
  }, [photos]);

  // Scale-to-fit state
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [lastSize, setLastSize] = useState({ cw: 0, ch: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const compute = () => {
      const el = containerRef.current;
      if (!el) return;
      const cw = el.clientWidth || 1;
      const ch = el.clientHeight || 1;
      
      if (fillContainer) {
        // When filling container, scale to fit both dimensions
        const scaleX = cw / pageWidth;
        const scaleY = ch / pageHeight;
        const s = Math.min(scaleX, scaleY); // Use smaller scale to fit
        setScale(Number.isFinite(s) && s > 0 ? s : 1);
      } else {
        // Normal behavior - scale based on width only
        const s = cw / pageWidth;
        setScale(Number.isFinite(s) && s > 0 ? s : 1);
      }
      
      setLastSize({ cw, ch });
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [pageWidth, pageHeight, debug, fillContainer]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, total]);

  const goPrev = () => {
    const next = index > 0 ? index - 1 : 0;
    setIndex(next);
    onChange && onChange(next);
  };
  
  const goNext = () => {
    const next = index + 1 < total ? index + 1 : index;
    setIndex(next);
    onChange && onChange(next);
  };

  const stageStyle = fillContainer ? {
    width: `${pageWidth}px`,
    height: `${pageHeight}px`,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px)) scale(${scale})`,
    transformOrigin: 'center center',
  } : {
    width: `${pageWidth}px`,
    height: `${pageHeight}px`,
    position: 'relative',
    transform: `scale(${scale})`,
    transformOrigin: 'top left',
  };

  const outerStyle = fillContainer ? {
    width: '100%',
    height: '100%',
    position: 'relative',  // ← Changed from 'absolute' to 'relative'
    top: 0,
    left: 0,
    margin: 0,
    overflow: 'hidden',
    // Removed flexbox - use transform instead
  } : {
  width: '100%',
  maxWidth: 'var(--album-max, 1100px)',
  aspectRatio: `${pageWidth} / ${pageHeight}`,
  margin: 'auto', // <-- CHANGED '0 auto' TO 'auto'
  position: 'relative',
  overflow: 'hidden',
};

  // ========== BUILD ALBUM DATA FOR PDF ==========
  const buildAlbumForPDF = () => {
    console.group('📦 Building Album for PDF');
    
    const simplePages = pages.map((p) => {
      const items = p?.items || [];
      
      // Get photo URLs from photoIndex
      const photoUrls = items
        .map(item => {
          const photoId = item?.photoId;
          const photoObj = photoId && photoIndex[photoId] ? photoIndex[photoId] : null;
          
          if (!photoObj) {
            console.warn('Photo not found:', photoId);
            return null;
          }
          
          // Use the URL directly (it's already base64 or blob)
          const url = photoObj.url;
          
          if (!url) {
            console.warn('Photo missing URL:', photoId);
            return null;
          }
          
          return { src: url };
        })
        .filter(photo => photo !== null);

      const caption = (p?.meta && (p.meta.caption || p.meta.title)) || '';
      
      return { caption, photos: photoUrls };
    });

    // Get cover image (first available photo)
    let coverImage = '';
    for (const page of simplePages) {
      if (page.photos && page.photos.length > 0) {
        coverImage = page.photos[0].src;
        break;
      }
    }

    const album = {
      title: 'My Album',
      coverImage,
      tripDates: '',
      tripRoute: '',
      pages: simplePages
    };
    
    // Verification
    const totalPhotos = simplePages.reduce(
      (sum, p) => sum + (p.photos?.length || 0), 
      0
    );
    
    console.log('✅ Album built successfully');
    console.log('   Pages:', album.pages.length);
    console.log('   Total photos:', totalPhotos);
    console.groupEnd();
    
    if (totalPhotos === 0) {
      throw new Error('No photos found in album. Please add photos to your pages.');
    }
    
    return album;
  };

  // ========== SUBSCRIPTION CHECK ==========
  const checkUserSubscription = () => {
    // ✅ UPDATED: Use isPro hook as primary, localStorage as fallback
    if (isPro !== undefined) return isPro;
    const hasSubscription = localStorage.getItem('vivida_subscription') === 'active';
    return hasSubscription;
  };

  // ========== PDF GENERATION ==========
  // =============================================================================
// SIMPLE & RELIABLE: Temporarily Set Scale to 1
// =============================================================================
// The simplest approach that works
// =============================================================================

const handleDownloadPDF = async () => {
  if (isGeneratingPDF) return;
  
  try {
    setIsGeneratingPDF(true);
    // ===== NEW: Set initial progress =====
    setPdfProgress({ current: 0, total: pages.length });
    // =======================================
    
    // ✅ UPDATED: Low-res PDF always has watermark
    // Pro benefit is access to print-quality PDF (no watermark)
    const hasSubscription = isPro;
    const addWatermark = true; // Always watermark low-res PDF
    
    // Show subscription modal for Free tier (but still allow medium-quality export)
    if (!isPro) {
      openUpgradeModal('pdf-quality');
    }
    
    // Quality tiers: Free vs Pro
    const pdfSettings = hasSubscription 
      ? { scale: 2.5, quality: 0.88, label: 'Pro Quality' }
      : { scale: 2, quality: 0.75, label: 'Standard' };
    
    console.log(`🎨 Generating PDF (${pdfSettings.label})...`);
    
    // Calculate PDF dimensions
    const albumAspectRatio = pageWidth / pageHeight;
    const basePdfSize = 8.5;
    
    let pdfWidth, pdfHeight;
    if (albumAspectRatio > 1) {
      pdfWidth = basePdfSize;
      pdfHeight = basePdfSize / albumAspectRatio;
    } else {
      pdfHeight = basePdfSize;
      pdfWidth = basePdfSize * albumAspectRatio;
    }
    
    console.log(`   PDF: ${pdfWidth.toFixed(2)}" × ${pdfHeight.toFixed(2)}"`);
    
    // Create PDF
    const { jsPDF } = await import('jspdf');
    const pdf = new jsPDF({
      orientation: albumAspectRatio > 1 ? 'landscape' : 'portrait',
      unit: 'in',
      format: [pdfWidth, pdfHeight],
      compress: true
    });
    
    // Store original index
    const originalIndex = index;
    
    // Find container and stage
    const previewContainer = containerRef.current;
    if (!previewContainer) {
      throw new Error('Preview container not found');
    }
    
    let isFirstPage = true;
    
    // Loop through pages
    for (let i = 0; i < pages.length; i++) {
      // ===== NEW: Update progress =====
      setPdfProgress({ current: i + 1, total: pages.length });
      // ================================
      
      console.log(`   Capturing page ${i + 1}/${pages.length}...`);
      
      // Set current page
      setIndex(i);
      
      // Wait for initial render
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Find stage div
      const stageDiv = previewContainer.querySelector('[style*="transform"]');
      if (!stageDiv) {
        console.warn('   ⚠️ Stage not found');
        continue;
      }
      
      // Wait for all canvases to finish drawing
      await waitForAllCanvasesToLoad(stageDiv);
      
      // CRITICAL: Force scale to 1 temporarily
      const originalTransform = stageDiv.style.transform;
      const originalWidth = stageDiv.style.width;
      const originalHeight = stageDiv.style.height;
      
      // Set to natural size
      stageDiv.style.transform = 'scale(1)';
      stageDiv.style.transformOrigin = 'top left';
      stageDiv.style.width = `${pageWidth}px`;
      stageDiv.style.height = `${pageHeight}px`;
      
      // Wait for reflow
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Capture at natural size with quality based on tier
      const canvas = await html2canvas(stageDiv, {
        scale: pdfSettings.scale,  // Quality tier: Free=2, Pro=2.5
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#faf8f5',
        width: pageWidth,
        height: pageHeight,
        logging: false
      });
      
      // Restore original styling immediately
      stageDiv.style.transform = originalTransform;
      stageDiv.style.width = originalWidth;
      stageDiv.style.height = originalHeight;
      
      console.log(`   ✓ Captured: ${canvas.width}×${canvas.height}px`);
      
      // Add to PDF
      if (!isFirstPage) {
        pdf.addPage();
      } else {
        isFirstPage = false;
      }
      
      // Use JPEG with quality based on tier
      const imgData = canvas.toDataURL('image/jpeg', pdfSettings.quality);
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      
      // Watermark for free users - Updated branding
if (addWatermark) {
  // 1. Large diagonal watermark
  pdf.saveGraphicsState();
  pdf.setGState(new pdf.GState({ opacity: 0.12 }));
  pdf.setTextColor(100, 100, 100);
  
  pdf.setFontSize(36);
  pdf.setFont(undefined, 'bold');
  pdf.text('VIVAJOUR', pdfWidth / 2, pdfHeight / 2, {
    align: 'center',
    angle: 45,
    baseline: 'middle'
  });
  pdf.restoreGraphicsState();
  
  // 2. Bottom watermark
  pdf.setTextColor(80, 80, 80);
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  pdf.text('Created with VivaJour • vivajour.com', pdfWidth / 2, pdfHeight - 0.2, {
    align: 'center'
  });
  
  // 3. Small corner badge
  pdf.setFillColor(255, 255, 255);
  pdf.setDrawColor(180, 180, 180);
  pdf.roundedRect(pdfWidth - 1.3, pdfHeight - 0.5, 1.2, 0.4, 0.05, 0.05, 'FD');
  pdf.setTextColor(100, 100, 100);
  pdf.setFontSize(7);
  pdf.text('Created with VivaJour', pdfWidth - 1.25, pdfHeight - 0.35, { align: 'left' });
  // replace back the Created with VivaJour with Upgrade for no watermark
  pdf.setFontSize(9);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(59, 130, 246); // Blue color
  pdf.text('vivajour.com', pdfWidth - 1.25, pdfHeight - 0.18, { align: 'left' });
}
    }
    
    // Restore original page
    setIndex(originalIndex);
    
    // Generate PDF blob
    const pdfBlob = pdf.output('blob');
    
    // Generate unique filename with full timestamp (YYYYMMDD-HHMMSS)
    const now = new Date();
    const timestamp = now.getFullYear().toString() +
      String(now.getMonth() + 1).padStart(2, '0') +
      String(now.getDate()).padStart(2, '0') + '-' +
      String(now.getHours()).padStart(2, '0') +
      String(now.getMinutes()).padStart(2, '0') +
      String(now.getSeconds()).padStart(2, '0');
    const fileName = `VivaJour-Album-${timestamp}.pdf`;
    
    // Platform-aware saving
    if (Capacitor.isNativePlatform()) {
      // Android/iOS: Save to Documents and offer to share
      try {
        await saveAndSharePDFNative(pdfBlob, fileName);
        console.log('✅ PDF saved via native filesystem');
      } catch (nativeError) {
        console.error('Native save failed, trying fallback:', nativeError);
        // Fallback: try the web method anyway (might work on some devices)
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
        alert('📄 PDF generated!\n\nCheck your Downloads folder.');
      }
    } else {
      // Web: Standard download
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      
      console.log('✅ PDF generated!');
      
      // Both free and pro users get watermarked low-res PDF
      // Pro benefit is access to print-quality PDF button
      if (hasSubscription) {
        alert('🎉 PDF downloaded!\n\nThis is the medium-quality version (watermarked).\n\nFor print-quality (no watermark), use the "Print-quality PDF" button.');
      } else {
        alert('🎉 PDF downloaded!\n\nThis is the medium-quality version (watermarked).\n\nUpgrade to Pro to unlock print-quality PDF exports (300 DPI, no watermark).');
      }
    }
    
  } catch (error) {
    console.error('❌ PDF generation failed:', error);
    alert('PDF generation failed: ' + error.message);
  } finally {
    setIsGeneratingPDF(false);
  }
};

// ========== SHARE ALBUM (Link) ==========
  // ========== SHARE ALBUM (Link) ==========


  // ========== PRINT QUALITY PDF (Subscription-Gated) ==========
  const handlePrintQualityPDF = async () => {
    if (isGeneratingPDF || isUploadingPrint) return;
    
    // ✅ Pro-gated: Free users must upgrade
    if (!isPro) {
      openUpgradeModal('pdf-quality');
      return;
    }
    
    // Pro users get high-quality PDF directly
    try {
      setIsGeneratingPDF(true);
      setPdfProgress({ current: 0, total: pages.length });
      
      console.log('🖨️ Generating PRINT-QUALITY PDF...');
      
      // Calculate PDF dimensions
      const albumAspectRatio = pageWidth / pageHeight;
      const basePdfSize = 8.5;
      
      let pdfWidth, pdfHeight;
      if (albumAspectRatio > 1) {
        pdfWidth = basePdfSize;
        pdfHeight = basePdfSize / albumAspectRatio;
      } else {
        pdfHeight = basePdfSize;
        pdfWidth = basePdfSize * albumAspectRatio;
      }
      
      console.log(`   PDF: ${pdfWidth.toFixed(2)}" × ${pdfHeight.toFixed(2)}"`);
      
      // Create PDF
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({
        orientation: albumAspectRatio > 1 ? 'landscape' : 'portrait',
        unit: 'in',
        format: [pdfWidth, pdfHeight],
        compress: true
      });
      
      const originalIndex = index;
      const previewContainer = containerRef.current;
      if (!previewContainer) {
        throw new Error('Preview container not found');
      }
      
      let isFirstPage = true;
      
      for (let i = 0; i < pages.length; i++) {
        setPdfProgress({ current: i + 1, total: pages.length });
        console.log(`   Capturing page ${i + 1}/${pages.length}...`);
        
        setIndex(i);
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const stageDiv = previewContainer.querySelector('[style*="transform"]');
        if (!stageDiv) {
          console.warn('   ⚠️ Stage not found');
          continue;
        }
        
        await waitForAllCanvasesToLoad(stageDiv);
        
        const originalTransform = stageDiv.style.transform;
        const originalWidth = stageDiv.style.width;
        const originalHeight = stageDiv.style.height;
        
        stageDiv.style.transform = 'scale(1)';
        stageDiv.style.transformOrigin = 'top left';
        stageDiv.style.width = `${pageWidth}px`;
        stageDiv.style.height = `${pageHeight}px`;
        
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // HIGH QUALITY: 3x scale for print
        const canvas = await html2canvas(stageDiv, {
          scale: 3,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#faf8f5',
          width: pageWidth,
          height: pageHeight,
          logging: false
        });
        
        stageDiv.style.transform = originalTransform;
        stageDiv.style.width = originalWidth;
        stageDiv.style.height = originalHeight;
        
        console.log(`   ✓ Captured: ${canvas.width}×${canvas.height}px`);
        
        if (!isFirstPage) {
          pdf.addPage();
        } else {
          isFirstPage = false;
        }
        
        const imgData = canvas.toDataURL('image/jpeg', 0.98);
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      }
      
      setIndex(originalIndex);
      
      // Download
      const pdfBlob = pdf.output('blob');
      const fileSizeMB = (pdfBlob.size / 1024 / 1024).toFixed(1);
      
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'my-album-print-quality.pdf';
      link.click();
      URL.revokeObjectURL(url);
      
      console.log('✅ Print-quality PDF generated!');
      
      alert(
        '✅ Print-quality PDF downloaded!\n\n' +
        `File size: ${fileSizeMB} MB\n\n` +
        'Optimized for:\n' +
        '• Blurb photobooks\n' +
        '• Printful printing\n' +
        '• Professional print shops\n\n' +
        '💡 This PDF has higher resolution and is ready for printing.'
      );
      
    } catch (error) {
      console.error('❌ Print PDF generation failed:', error);
      alert('Failed to generate print-quality PDF: ' + error.message);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // ========== ADMIN: PRINT-QUALITY PDF ==========
  const handleAdminPrintPDF = async () => {
    if (isGeneratingPDF || isUploadingPrint) return;
    
    // Password check
    if (!checkAdminPassword()) {
      alert('❌ Incorrect password');
      return;
    }
    
    // Ask what to do with the PDF
    const choice = window.confirm(
      '🖨️ Print-Quality PDF\n\n' +
      'Choose an option:\n\n' +
      '• OK = Upload to Firebase + Download locally\n' +
      '• Cancel = Download locally only'
    );
    const uploadToFirebase = choice;
    
    try {
      setIsUploadingPrint(true);
      setIsGeneratingPDF(true);
      setPdfProgress({ current: 0, total: pages.length });
      
      console.log('🖨️ ADMIN: Generating print-quality PDF...');
      console.log(`   Settings: scale=${PRINT_SETTINGS.scale}, quality=${PRINT_SETTINGS.quality}`);
      
      const albumAspectRatio = pageWidth / pageHeight;
      const basePdfSize = 8.5;
      
      let pdfWidth, pdfHeight;
      if (albumAspectRatio > 1) {
        pdfWidth = basePdfSize;
        pdfHeight = basePdfSize / albumAspectRatio;
      } else {
        pdfHeight = basePdfSize;
        pdfWidth = basePdfSize * albumAspectRatio;
      }
      
      console.log(`   PDF size: ${pdfWidth.toFixed(2)}" × ${pdfHeight.toFixed(2)}"`);
      
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({
        orientation: albumAspectRatio > 1 ? 'landscape' : 'portrait',
        unit: 'in',
        format: [pdfWidth, pdfHeight],
        compress: true
      });
      
      const originalIndex = index;
      const previewContainer = containerRef.current;
      if (!previewContainer) throw new Error('Preview container not found');
      
      let isFirstPage = true;
      
      for (let i = 0; i < pages.length; i++) {
        setPdfProgress({ current: i + 1, total: pages.length });
        console.log(`   📄 Page ${i + 1}/${pages.length} (print quality)...`);
        
        setIndex(i);
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const stageDiv = previewContainer.querySelector('[style*="transform"]');
        if (!stageDiv) continue;
        
        await waitForAllCanvasesToLoad(stageDiv);
        
        const originalTransform = stageDiv.style.transform;
        const originalWidth = stageDiv.style.width;
        const originalHeight = stageDiv.style.height;
        
        stageDiv.style.transform = 'scale(1)';
        stageDiv.style.transformOrigin = 'top left';
        stageDiv.style.width = `${pageWidth}px`;
        stageDiv.style.height = `${pageHeight}px`;
        
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // PRINT QUALITY: 4x scale for ~300 DPI
        const canvas = await html2canvas(stageDiv, {
          scale: PRINT_SETTINGS.scale,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#faf8f5',
          width: pageWidth,
          height: pageHeight,
          logging: false
        });
        
        stageDiv.style.transform = originalTransform;
        stageDiv.style.width = originalWidth;
        stageDiv.style.height = originalHeight;
        
        console.log(`   ✓ ${canvas.width}×${canvas.height}px`);
        
        if (!isFirstPage) pdf.addPage();
        else isFirstPage = false;
        
        const imgData = canvas.toDataURL('image/jpeg', PRINT_SETTINGS.quality);
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
        // NO WATERMARK for print quality
      }
      
      setIndex(originalIndex);
      
      const pdfBlob = pdf.output('blob');
      const sizeMB = (pdfBlob.size / 1024 / 1024).toFixed(2);
      console.log(`✅ Print PDF: ${sizeMB} MB`);
      
      const now = new Date();
      const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const fileName = `VivaJour-Print-${timestamp}.pdf`;
      
      let downloadURL = null;
      let storagePath = null;
      
      // Upload to Firebase if requested
      if (uploadToFirebase) {
        console.log('📤 Uploading to Firebase Storage...');
        
        const app = getApp();
        const storage = getStorage(app);
        
        storagePath = `print-orders/${timestamp}/${fileName}`;
        
        await uploadBytes(ref(storage, storagePath), pdfBlob, {
          contentType: 'application/pdf',
          customMetadata: { 
            quality: 'print-300dpi', 
            pages: String(pages.length),
            scale: String(PRINT_SETTINGS.scale)
          }
        });
        
        downloadURL = await getDownloadURL(ref(storage, storagePath));
        
        console.log('✅ Uploaded!');
        console.log(`   Path: ${storagePath}`);
        console.log(`   URL: ${downloadURL}`);
        
        // Copy Firebase URL to clipboard
        try {
          await navigator.clipboard.writeText(downloadURL);
          console.log('📋 URL copied to clipboard');
        } catch (e) {
          console.log('Could not copy to clipboard');
        }
      }
      
      // Always download locally
      console.log('💾 Downloading locally...');
      const localUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = localUrl;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(localUrl);
      
      // Show success message
      if (uploadToFirebase && downloadURL) {
        alert(
          `🖨️ PRINT PDF READY!\n\n` +
          `📊 Size: ${sizeMB} MB\n` +
          `📄 Pages: ${pages.length}\n` +
          `🔍 Quality: ${PRINT_SETTINGS.label}\n\n` +
          `💾 Downloaded locally: ${fileName}\n\n` +
          `☁️ Firebase Path:\n${storagePath}\n\n` +
          `🔗 URL copied to clipboard!\n${downloadURL.slice(0, 60)}...`
        );
      } else {
        alert(
          `🖨️ PRINT PDF DOWNLOADED!\n\n` +
          `📊 Size: ${sizeMB} MB\n` +
          `📄 Pages: ${pages.length}\n` +
          `🔍 Quality: ${PRINT_SETTINGS.label}\n\n` +
          `💾 File: ${fileName}`
        );
      }
      
    } catch (error) {
      console.error('❌ Admin print failed:', error);
      alert(`Print PDF generation failed: ${error.message}`);
    } finally {
      setIsGeneratingPDF(false);
      setIsUploadingPrint(false);
    }
  };

  // Long-press handlers for admin print
  const handlePrintButtonTouchStart = () => {
    isLongPressRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      isLongPressRef.current = true;
      if (navigator.vibrate) navigator.vibrate(50);
      console.log('🔓 Admin print triggered via long-press');
      handleAdminPrintPDF();
    }, 1500);
  };

  const handlePrintButtonTouchEnd = (e) => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (isLongPressRef.current) {
      e.preventDefault();
      isLongPressRef.current = false;
    }
  };

  const handlePrintButtonClick = (e) => {
    if (isLongPressRef.current) {
      isLongPressRef.current = false;
      return;
    }
    handleOrderPrintedBook();
  };

  // ========== ORDER PRINTED BOOK ==========
  const handleOrderPrintedBook = async () => {
    // Check subscription for discount info
    const hasSubscription = checkUserSubscription();
    
    const message = hasSubscription
      ? '📚 Order a Printed Book\n\n' +
       // '🎁 As a Pro member, you get 15% off all printed books!\n\n' +
        'We\'re partnering with professional print services to offer:\n\n' +
        '   • 8"×8" Premium Photo Book\n' +
        '   • High-quality layflat pages\n' +
        '   • Durable hardcover\n' +
        '   • Ships worldwide\n\n' +
        'Want to be notified when printing is available?'
      : '📚 Order a Printed Book\n\n' +
        'We\'re partnering with professional print services to offer:\n\n' +
        '   • 8"×8" Premium Photo Book\n' +
        '   • High-quality layflat pages\n' +
        '   • Durable hardcover\n' +
        '   • Ships worldwide\n\n' +
       // '✨ Pro members get 15% off all printed books!\n\n' +
        'Want to be notified when printing is available?';
    
    const wantsNotification = window.confirm(message);
    
    if (wantsNotification) {
      // For now, just show confirmation. Later this could collect email or link to waitlist
      alert(
        '✅ Thanks for your interest!\n\n' +
        'We\'ll announce printing availability soon.\n\n' +
        'Follow us for updates:\n' +
        '   • vivajour.com\n' +
        '   • @vivajour on social media'
      );
    }
  };

  return (
    <div className="w-full">
      {/* Navigation */}
      {showNav && (
        <div className="flex items-center justify-between mb-3">
          <button 
            className="px-3 py-1 rounded border" 
            onClick={goPrev} 
            disabled={index === 0}
          >
            ‹ Prev
          </button>
          <div className="text-sm opacity-70">
            {page ? index + 1 : 0}/{total}
          </div>
          <button 
            className="px-3 py-1 rounded border" 
            onClick={goNext} 
            disabled={index + 1 > total - 1}
          >
            Next ›
          </button>
        </div>
      )}

      {/* Preview - CLEAN: No overlay inside this container anymore */}
      <div
        ref={containerRef}
        className={fillContainer 
          ? "" 
          : "relative mx-auto bg-neutral-100 rounded-xl shadow overflow-hidden"
        }
        style={outerStyle}
      >
        <div style={stageStyle}>
  {page ? (
    <RenderPage
      page={page}
      photoIndex={photoIndex}
      isPrinting={isGeneratingPDF} 
    />
  ) : null}

  {debug && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                outline: '2px dashed rgba(16,185,129,0.8)',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
        
        {/* Debug info only - overlay moved outside */}
        {debug && (
          <div
            style={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              background: 'rgba(0,0,0,0.6)',
              color: 'white',
              fontSize: 12,
              padding: '4px 6px',
              borderRadius: 6,
            }}
          >
            cw×ch: {lastSize.cw}×{lastSize.ch} · scale: {scale.toFixed(3)}
          </div>
        )}
      </div>

      {/* ===== PDF GENERATION OVERLAY - MOVED OUTSIDE containerRef ===== */}
      {/* Using position:fixed to cover entire viewport without affecting album layout */}
      {isGeneratingPDF && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.95)', // Slightly more opaque for full screen
          backdropFilter: 'blur(8px)',
          zIndex: 9999, // High z-index to be above everything
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
        }}>
          
          <h3 style={{
            fontSize: '1.5rem', // 2xl
            fontWeight: 700,
            color: 'white',
            marginBottom: '1rem'
          }}>
            {isUploadingPrint ? 'Generating Print-Quality PDF' : 'Generating Your Album'}
          </h3>
          
          <p style={{
            fontSize: '1.125rem', // lg
            color: '#cbd5e1', // slate-300
            marginBottom: '1rem'
          }}>
            Processing Page {pdfProgress.current} of {pdfProgress.total}
            {isUploadingPrint && ' (300 DPI)'}
          </p>
          
          {/* The Mini-Flipper - shows current page being processed */}
          <div style={{
            width: '360px',
            maxWidth: '90vw',
            height: '240px',
            overflow: 'hidden',
            borderRadius: '8px',
            boxShadow: '0 10px 15px rgba(0,0,0,0.2)',
            border: '2px solid rgba(255,255,255,0.2)',
            backgroundColor: '#f9f7f5' // Match page background
          }}>
            {/* We render a *second*, scaled-down, read-only version.
              It uses the *same* `index` state, so it flips in sync
              with the hidden, full-size one.
            */}
            {pages[index] && (() => {
              const MAX_WIDTH = 360;
              const MAX_HEIGHT = 240;

              // Current behavior: scale based on width only
              const baseScale = MAX_WIDTH / pageWidth;
              let miniScale = baseScale;

              // On Android only, avoid making the scaled height taller than 240px
              if (isAndroid) {
                const scaledHeight = pageHeight * baseScale;
                if (scaledHeight > MAX_HEIGHT) {
                  const heightScale = MAX_HEIGHT / pageHeight;
                  miniScale = Math.min(baseScale, heightScale);
                }
              }

              return (
                <div
                  style={{
                    width: `${pageWidth}px`,
                    height: `${pageHeight}px`,
                    transform: `scale(${miniScale})`,
                    transformOrigin: 'top left',
                  }}
                >
                  <RenderPage
                    page={pages[index]}
                    photoIndex={photoIndex}
                    isPrinting={true}
                  />
                </div>
              );
            })()}
          </div>
          
          {/* Progress Bar */}
          <div style={{
            width: 'min(360px, 90%)',
            backgroundColor: '#334155', // slate-700
            borderRadius: '9999px',
            height: '10px',
            marginTop: '2rem'
          }}>
            <div
              style={{
                backgroundColor: isUploadingPrint ? '#9333ea' : '#2563eb', // purple for print, blue for normal
                height: '10px',
                borderRadius: '9999px',
                transition: 'width 0.15s ease-out', // Smooth progress
                width: `${(pdfProgress.current / (pdfProgress.total || 1)) * 100}%`
              }}
            ></div>
          </div>
          
          {/* Helpful tip */}
          <p style={{
            fontSize: '0.875rem',
            color: '#94a3b8', // slate-400
            marginTop: '1.5rem',
            textAlign: 'center'
          }}>
            {isUploadingPrint 
              ? 'Generating high-resolution pages for printing...'
              : 'Please wait while we capture each page...'}
          </p>
        </div>
      )}
      {/* ===== END OF OVERLAY ===== */}

      {/* Action Buttons - Placed BELOW the album preview */}
      {showButtons && (
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          
          {/* 1. Preview Story - Quick, no commitment */}
          <button
            type="button"
            className="flex-1 px-4 py-2 rounded bg-slate-700 text-white hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => onPreview && onPreview()}
            disabled={previewBusy}
          >
            <div className="flex items-center justify-center gap-2">
              {previewBusy ? (
                <>
                  <span className="animate-spin">⏳</span>
                  <span>Building Story...</span>
                </>
              ) : (
                <>
                  <span>▶️</span>
                  <span>Preview Story</span>
                </>
              )}
            </div>
            <div className="text-xs opacity-80 mt-1">
              {previewBusy ? 'Preparing your album...' : 'Opens story in a new tab'}
            </div>
          </button>

          {/* 2. Share - Primary action */}
          <button
            type="button"
            className="flex-1 px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => onShare && onShare()}
            disabled={shareBusy}
          >
            <div className="flex items-center justify-center gap-2">
              {shareBusy ? (
                <>
                  <span className="animate-spin">⏳</span>
                  <span>Preparing link…</span>
                </>
              ) : (
                <>
                  <span>🔗</span>
                  <span>Share</span>
                </>
              )}
            </div>
            <div className="text-xs opacity-80 mt-1">
              {shareBusy ? 'Talking to server' : 'Create shareable story link'}
            </div>
          </button>

          {/* 3. Download PDF - Secondary action */}
          <button
            type="button"
            className="flex-1 px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            onClick={handleDownloadPDF}
            disabled={isGeneratingPDF}
          >
            <div className="flex items-center justify-center gap-2">
              {isGeneratingPDF && !isUploadingPrint ? (
                <>
                  <span className="animate-spin">⏳</span>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <span>📄</span>
                  <span>Download PDF</span>
                </>
              )}
            </div>
            {!isGeneratingPDF && (
              <div className="text-xs opacity-80 mt-1">
                {!isPro
                  ? 'Medium-quality (watermarked)'
                  : 'Medium-quality (watermarked) • Use Print-quality PDF for best results'}
              </div>
            )}
          </button>

          {/* 4. Print-quality PDF - Subscription gated (with admin backdoor) */}
          <button
            type="button"
            className="flex-1 px-4 py-2 rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            onClick={handlePrintQualityPDF}
            onTouchStart={handlePrintButtonTouchStart}
            onTouchEnd={handlePrintButtonTouchEnd}
            onMouseDown={handlePrintButtonTouchStart}
            onMouseUp={handlePrintButtonTouchEnd}
            onMouseLeave={handlePrintButtonTouchEnd}
            disabled={isGeneratingPDF || isUploadingPrint}
          >
            <div className="flex items-center justify-center gap-2">
              {isUploadingPrint ? (
                <>
                  <span className="animate-spin">⏳</span>
                  <span>Uploading Print PDF...</span>
                </>
              ) : (
                <>
                  <span>🖨️</span>
                  <span>Print-quality PDF</span>
                </>
              )}
            </div>
            <div className="text-xs opacity-80 mt-1">
              {isUploadingPrint 
                ? `Page ${pdfProgress.current}/${pdfProgress.total}` 
                : (!isPro ? 'Pro feature' : '300 DPI • Best for printing')}
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

function RenderPage({ page, photoIndex, isPrinting = false }) { // <-- 1. ADDED isPrinting
  const template = templateFor(page.templateKey || page.type);

  if (!template || !template.component) {
    return (
      <div style={{ padding: 20, background: '#fee2e2', color: '#991b1b' }}>
        Error: Template "{page.templateKey}" not found in manifest.
      </div>
    );
  }

  const PageComponent = template.component;
  const itemsWithUrls = toUrls(page.items, photoIndex);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <PageComponent
        items={itemsWithUrls}
        meta={page.meta}
        {...template.props}
        isPrinting={isPrinting} // <-- 2. ADDED isPrinting
      />
    </div>
  );
}

function toUrls(items = [], photoIndex = {}) {
  if (!Array.isArray(items)) return [];
  
  if (items.length && items[0] && 'url' in items[0]) {
    return items;
  }
  
  return items.map((item) => {
    const photoId = item?.photoId;
    const photoObj = photoId && photoIndex[photoId] ? photoIndex[photoId] : null;
    
    if (photoObj) {
      return {
        url: photoObj.url,
        position: photoObj.position || item?.position || { x: 50, y: 50, scale: 100 },
        id: photoObj.id,
        ...item
      };
    }
    
    return {
      url: '',
      position: item?.position || { x: 50, y: 50, scale: 100 },
      ...item
    };
  });
}

function safeIndex(n, len) {
  if (!len) return 0;
  if (n < 0) return 0;
  if (n > len - 1) return len - 1;
  return n;
}

export { RenderPage }